// apps/client/src/components/reports/LoanBookValuePanel.tsx
//
// Phases 3 and 6 of docs/handovers/2026-08-04-LOAN-BOOK-VS-LMG-RECONCILIATION.md.
//
// Replaces the single unlabelled "Loan Book Value" card on the Broker
// Performance Report. Four things this panel must always do:
//
//   1. State its entity scope. A loan book number without its scope label was
//      the root cause of the incident, so the label renders in every state,
//      including loading-free static renders and the hidden-dollar case.
//   2. Never silently hide value. In "Primary loan book" mode, value held in
//      other entities is called out on its own persistent line.
//   3. Never blend confirmed balances with estimated ones. Estimated means the
//      lender never reported a balance and the original settled amount stands
//      in, which does not amortise and reads high.
//   4. Show how current the figures are, per entity. Entities file at different
//      times, so an aggregate routinely mixes as-at dates.
//
// Dollar suppression: hidden-commission users see the standard placeholder in
// place of every dollar figure while the loan COUNTS still render, per
// docs/handovers/2026-07-11-LOAN-VALUE-VISIBILITY-POLICY.md.

import React from 'react';
import { AlertTriangle, Vault } from 'lucide-react';
import { formatCompactCurrency } from '../../utils/formatters';
import type { LoanBookMode, LoanBookTier, LoanBookView } from '../../types/loanBookView';
import {
  hasHiddenOtherEntityValue,
  hasMixedAsAtDates,
  isPrimaryUnset,
  scopeLabel,
} from '../../types/loanBookView';
import { formatCommissionValue } from '../../utils/commissionDisplay';
import { LoanBookScopeToggle } from './LoanBookScopeToggle';

// --- formatting -------------------------------------------------------------

/** Money arrives as integer cents everywhere. Convert only here. */
export function formatCents(cents: number): string {
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(cents / 100);
}

export function formatCentsCompact(cents: number): string {
  // Same three-significant-figure rule as every stat card (utils/formatters).
  return formatCompactCurrency(cents);
}

const MONTH_ABBREVIATIONS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

/**
 * 'YYYY-MM-DD' (or a BigQuery `{ value }` wrapper) to '30 Jun 2026'.
 *
 * Formatted from the parts rather than through `toLocaleDateString`, whose
 * `month: 'short'` output varies with the runtime's ICU build (Node without
 * full ICU renders 'June'). An as-at date on a customer-facing report has to
 * read the same everywhere.
 */
export function formatAsAt(asAt: string | { value?: string } | null | undefined): string {
  if (!asAt) return 'Not filed';
  const raw = typeof asAt === 'object' ? asAt.value : asAt;
  if (!raw) return 'Not filed';
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(raw);
  if (!match) return String(raw);
  const [, year, month, day] = match;
  const abbreviation = MONTH_ABBREVIATIONS[Number(month) - 1];
  if (!abbreviation) return String(raw);
  return `${day} ${abbreviation} ${year}`;
}

function plural(count: number, noun: string): string {
  return `${count.toLocaleString('en-AU')} ${noun}${count === 1 ? '' : 's'}`;
}

// --- props ------------------------------------------------------------------

export interface LoanBookValuePanelProps {
  view: LoanBookView;
  onModeChange: (mode: LoanBookMode) => void;
  /** Suppress every dollar figure. Loan counts still render. */
  hideCommissions?: boolean;
  /** Help affordance. Authed pages pass <HelpButton pageKey="loanBookValue" />. */
  helpSlot?: React.ReactNode;
  /** Disables the toggle while a mode change is in flight. */
  busy?: boolean;
}

// --- sub-components ---------------------------------------------------------

const CompositionRow: React.FC<{
  testId: string;
  label: string;
  note?: string;
  tier: LoanBookTier;
  hideCommissions: boolean;
  /** Amber treatment: this value is an estimate, not a lender-reported balance. */
  estimated?: boolean;
  /**
   * Show the "Estimated" chip. Off for the tier whose own label is already
   * "Estimated", so the row does not read "Estimated Estimated".
   */
  estimatedBadge?: boolean;
}> = ({ testId, label, note, tier, hideCommissions, estimated, estimatedBadge }) => (
  <div
    data-testid={testId}
    data-estimated={estimated ? 'true' : 'false'}
    className={`flex items-baseline justify-between gap-3 py-2 ${
      estimated ? 'border-l-2 border-amber-500/60 pl-3 -ml-3' : ''
    }`}
  >
    <div className="min-w-0">
      <div className={`text-sm ${estimated ? 'text-amber-300' : 'text-gray-300'}`}>
        {label}
        {estimatedBadge && (
          <span className="ml-2 align-middle text-[0.625rem] font-semibold uppercase tracking-wider text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded px-1.5 py-0.5">
            Estimated
          </span>
        )}
      </div>
      {note && <div className="text-xs text-gray-500 mt-0.5">{note}</div>}
    </div>
    <div className="text-right flex-shrink-0">
      <div
        className={`font-mono text-sm font-medium ${estimated ? 'text-amber-300' : 'text-white'}`}
      >
        {formatCommissionValue(tier.cents, hideCommissions, formatCents)}
      </div>
      <div className="text-xs text-gray-500">{plural(tier.loanCount, 'loan')}</div>
    </div>
  </div>
);

// --- panel ------------------------------------------------------------------

export const LoanBookValuePanel: React.FC<LoanBookValuePanelProps> = ({
  view,
  onModeChange,
  hideCommissions = false,
  helpSlot,
  busy = false,
}) => {
  const primaryUnset = isPrimaryUnset(view);
  const showOtherEntities = hasHiddenOtherEntityValue(view);
  const mixedDates = hasMixedAsAtDates(view);
  const label = scopeLabel(view);

  const entityLabelFor = (entityCode: string): string =>
    view.primaryEntity && view.primaryEntity.code === entityCode
      ? `${view.primaryEntity.label} (${entityCode})`
      : entityCode;

  return (
    <section data-testid="loan-book-panel">
      {/* Heading row: title, scope toggle, help */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <span className="text-[0.8125rem] font-semibold uppercase tracking-[0.125rem] text-gray-500">
          Loan Book
        </span>
        <div className="flex-1 min-w-[2rem] h-px bg-white/[0.06]" />
        <LoanBookScopeToggle
          mode={view.mode}
          onModeChange={onModeChange}
          primaryEntity={view.primaryEntity}
          disabled={busy}
        />
        {helpSlot}
      </div>

      <div className="bg-gray-800/50 border border-white/[0.06] rounded-xl overflow-hidden">
        {/* Headline */}
        <div className="relative px-5 py-5 overflow-hidden">
          <div className="absolute top-0 left-0 right-0 h-[0.1875rem]" style={{ background: '#f59e0b' }} />
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2 text-[0.6875rem] font-medium uppercase tracking-[0.075rem] text-gray-500 mb-2">
                <Vault className="w-3.5 h-3.5 text-amber-400" aria-hidden="true" />
                Trail-paying loan book
              </div>
              <div
                data-testid="loan-book-headline"
                className="text-3xl font-bold leading-none mb-1"
                style={{ color: '#f59e0b' }}
              >
                {formatCommissionValue(view.headlineCents, hideCommissions, formatCentsCompact)}
              </div>
              <div className="text-sm text-gray-400">
                {plural(view.activeLoans, 'loan')} paying trail
              </div>
            </div>

            {/* Scope label. Renders in every state. */}
            <span
              data-testid="loan-book-scope-label"
              className="inline-block bg-white/5 border border-white/10 rounded-md px-2.5 py-1 text-[0.6875rem] uppercase tracking-widest text-gray-300"
            >
              {label}
            </span>
          </div>

          {/* Value sitting outside the primary entity. Never silently hidden. */}
          {showOtherEntities && (
            <div
              data-testid="loan-book-other-entities"
              className="mt-3 text-sm text-blue-300 bg-blue-500/[0.07] border border-blue-500/20 rounded-lg px-3 py-2"
            >
              {'+ '}
              {formatCommissionValue(view.otherEntitiesCents, hideCommissions, formatCentsCompact)}
              {' in other loan books. Switch to All loan books to include it.'}
            </div>
          )}

          {/* No primary configured. Do not guess one. */}
          {primaryUnset && (
            <div
              data-testid="loan-book-primary-unset"
              className="mt-3 flex items-start gap-2 text-sm text-amber-300 bg-amber-500/[0.07] border border-amber-500/20 rounded-lg px-3 py-2"
            >
              <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" aria-hidden="true" />
              <span>
                Primary loan book not set. Every lending entity for this broker is included, so this
                figure will not line up with a single Loan Market entity report. Set a primary
                lending entity to compare like for like.
              </span>
            </div>
          )}
        </div>

        {/* Composition. Separate lines, never blended. */}
        <div className="px-5 py-3 border-t border-white/[0.06] divide-y divide-white/[0.04]">
          <div className="text-[0.6875rem] font-medium uppercase tracking-[0.075rem] text-gray-500 pb-2">
            Outside the headline
          </div>
          <CompositionRow
            testId="loan-book-new-settlements"
            label="Settled, trail not yet started"
            note="Settled, but the lender has not paid a first trail cycle yet."
            tier={view.newSettlements}
            hideCommissions={hideCommissions}
          />

          <div className="text-[0.6875rem] font-medium uppercase tracking-[0.075rem] text-gray-500 pt-3 pb-2">
            How the headline is valued
          </div>
          <CompositionRow
            testId="loan-book-balance-tier"
            label="Confirmed balances"
            note="Most recent outstanding balance reported by the lender."
            tier={view.balanceTier}
            hideCommissions={hideCommissions}
          />
          <CompositionRow
            testId="loan-book-fallback-tier"
            label="Estimated"
            note="The lender never reported a balance, so the original settled amount is used. It does not reduce as the loan is paid down and will read high."
            tier={view.fallbackTier}
            hideCommissions={hideCommissions}
            estimated
          />

          {view.acquiredTrail && (
            <CompositionRow
              testId="loan-book-acquired-trail"
              label="Acquired trail"
              note="A purchased trail book. Valued on settled amounts, not lender balances."
              tier={view.acquiredTrail}
              hideCommissions={hideCommissions}
              estimated
              estimatedBadge
            />
          )}
        </div>

        {/* Freshness, per entity. */}
        <div className="px-5 py-3 border-t border-white/[0.06]" data-testid="loan-book-freshness">
          <div className="flex items-center justify-between gap-3 mb-2">
            <span className="text-[0.6875rem] font-medium uppercase tracking-[0.075rem] text-gray-500">
              As at
            </span>
            {mixedDates && (
              <span
                data-testid="loan-book-mixed-dates"
                className="text-xs text-gray-400"
              >
                Entities file at different times, so this total mixes as-at dates.
              </span>
            )}
          </div>
          {view.freshness.length === 0 ? (
            <div className="text-sm text-gray-500">No filing dates available.</div>
          ) : (
            <ul className="space-y-1.5">
              {view.freshness.map((f) => (
                <li
                  key={f.entityCode}
                  data-testid={`loan-book-freshness-${f.entityCode}`}
                  className="flex flex-wrap items-center justify-between gap-2 text-sm"
                >
                  <span className="text-gray-300">{entityLabelFor(f.entityCode)}</span>
                  <span className="flex items-center gap-2">
                    <span className="text-gray-400 font-mono text-xs">{formatAsAt(f.asAt)}</span>
                    {f.stale && (
                      <span
                        data-testid={`loan-book-stale-${f.entityCode}`}
                        title="This entity has not filed its commission file for the current month, so it is showing its previous month’s position."
                        className="inline-flex items-center gap-1 text-[0.6875rem] font-semibold uppercase tracking-wider text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded px-1.5 py-0.5"
                      >
                        <AlertTriangle className="w-3 h-3" aria-hidden="true" />
                        Not filed this month
                      </span>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </section>
  );
};

export default LoanBookValuePanel;
